/**
 * Saadadhaga storefront helpers for the Horizon theme.
 * Loaded as a module from layout/theme.liquid and mapped as `@theme/sd` in snippets/scripts.liquid.
 *
 * - <sd-sticky-video>: floating product video (sections/sd-product-sticky-video.liquid).
 * - <sd-variant-sync>: re-renders itself from the section HTML Horizon fetches on variant change.
 * - <sd-delivery-date>: "delivered between X and Y" in working days (Sundays skipped).
 * - <sd-size-chart>: inches / cm switch for the built-in size chart.
 * - <sd-buy-bar>: product buy bar and size check (sections/sd-product-buy-bar.liquid).
 */
import { StandardEvents } from '@shopify/events';

const MOBILE_QUERY = '(max-width: 749px)';
const DRAG_THRESHOLD = 6;
const FOOTER_GAP = 220;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

/**
 * Floating picture-in-picture product video.
 * Shows after the shopper scrolls past data-trigger px, hides near the footer, and stays
 * closed for the browser session once dismissed (data-dismiss-key, per product).
 * No video bytes load until it is first shown; the poster stands in until then.
 */
class SdStickyVideo extends HTMLElement {
  #video = null;
  #sound = null;
  #close = null;
  #dismissed = false;
  #sourceLoaded = false;
  #hideTimer = 0;
  #drag = { active: false, moved: false, startX: 0, startY: 0, left: 0, top: 0, pointerId: null };
  #reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  connectedCallback() {
    this.#video = this.querySelector('.sd-psv__video');
    this.#sound = this.querySelector('.sd-psv__sound');
    this.#close = this.querySelector('.sd-psv__close');
    if (!this.#video) return;

    try {
      this.#dismissed = sessionStorage.getItem(this.dataset.dismissKey) === '1';
    } catch {}

    this.#sound?.addEventListener('click', this.#onSound);
    this.#close?.addEventListener('click', this.#onClose);
    this.#video.addEventListener('click', this.#onVideoClick);
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('pointermove', this.#onPointerMove);
    this.addEventListener('pointerup', this.#onPointerEnd);
    this.addEventListener('pointercancel', this.#onPointerEnd);

    if (window.Shopify?.designMode) {
      // Always visible in the theme editor so it can be styled.
      this.#reveal();
      return;
    }

    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });
    this.#onScroll();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
    clearTimeout(this.#hideTimer);
    this.#video?.pause();
  }

  get #canShow() {
    if (this.#dismissed) return false;
    if (this.dataset.mobile === 'false' && window.matchMedia(MOBILE_QUERY).matches) return false;
    return true;
  }

  get #nearFooter() {
    return window.innerHeight + window.scrollY >= document.body.offsetHeight - FOOTER_GAP;
  }

  #onScroll = () => {
    const trigger = parseInt(this.dataset.trigger, 10) || 0;
    if (this.#canShow && window.scrollY > trigger && !this.#nearFooter) this.#reveal();
    else this.#conceal();
  };

  #loadSource() {
    if (this.#sourceLoaded) return;
    this.#sourceLoaded = true;
    const src = this.#video.dataset.src;
    if (!src) return;
    const source = document.createElement('source');
    source.src = src;
    source.type = this.#video.dataset.type || 'video/mp4';
    this.#video.append(source);
    this.#video.load();
  }

  #play() {
    if (this.#reduceMotion && this.#video.muted) return;
    this.#video.play()?.catch(() => {});
  }

  #reveal() {
    clearTimeout(this.#hideTimer);
    if (this.classList.contains('is-visible')) return;
    this.#loadSource();
    this.hidden = false;
    requestAnimationFrame(() => this.classList.add('is-visible'));
    this.#play();
  }

  #conceal() {
    if (this.hidden) return;
    this.classList.remove('is-visible');
    this.#video.pause();
    clearTimeout(this.#hideTimer);
    this.#hideTimer = setTimeout(() => {
      if (!this.classList.contains('is-visible')) this.hidden = true;
    }, 320);
  }

  #onSound = (event) => {
    event.stopPropagation();
    const on = this.#video.muted;
    this.#video.muted = !on;
    this.#sound.setAttribute('aria-pressed', String(on));
    this.#sound.setAttribute('aria-label', on ? 'Turn sound off' : 'Turn sound on');
    this.classList.toggle('is-unmuted', on);
    if (on) this.#video.play()?.catch(() => {});
  };

  #onClose = (event) => {
    event.stopPropagation();
    this.#dismissed = true;
    try {
      sessionStorage.setItem(this.dataset.dismissKey, '1');
    } catch {}
    this.#conceal();
  };

  // Tap to enlarge, unless the tap ended a drag.
  #onVideoClick = () => {
    if (this.#drag.moved) {
      this.#drag.moved = false;
      return;
    }
    this.classList.toggle('is-expanded');
  };

  #onPointerDown = (event) => {
    if (event.target.closest('.sd-psv__close, .sd-psv__sound')) return;
    const rect = this.getBoundingClientRect();
    this.#drag = {
      active: true,
      moved: false,
      startX: event.clientX,
      startY: event.clientY,
      left: rect.left,
      top: rect.top,
      pointerId: event.pointerId,
    };
    try {
      this.setPointerCapture(event.pointerId);
    } catch {}
  };

  #onPointerMove = (event) => {
    const drag = this.#drag;
    if (!drag.active) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (!drag.moved && (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD)) {
      drag.moved = true;
      this.classList.add('is-dragging');
    }
    if (!drag.moved) return;
    event.preventDefault();
    this.style.left = `${clamp(drag.left + dx, 8, window.innerWidth - this.offsetWidth - 8)}px`;
    this.style.top = `${clamp(drag.top + dy, 8, window.innerHeight - this.offsetHeight - 8)}px`;
    this.style.right = 'auto';
    this.style.bottom = 'auto';
  };

  #onPointerEnd = () => {
    if (!this.#drag.active) return;
    this.#drag.active = false;
    this.classList.remove('is-dragging');
    try {
      this.releasePointerCapture(this.#drag.pointerId);
    } catch {}
  };
}

/**
 * Replaces its contents with the freshly rendered copy after a variant change.
 * Markup: <sd-variant-sync data-sync-id="{{ block.id }}">…</sd-variant-sync>
 */
class SdVariantSync extends HTMLElement {
  #section = null;

  connectedCallback() {
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        const fresh = detail?.html?.querySelector(`sd-variant-sync[data-sync-id="${this.dataset.syncId}"]`);
        if (fresh) this.innerHTML = fresh.innerHTML;
      })
      .catch(() => {});
  };
}

/**
 * Renders a delivery window counted in working days from today.
 * Attributes: data-min, data-max (working days), data-template ("{from}" / "{to}" placeholders).
 */
class SdDeliveryDate extends HTMLElement {
  connectedCallback() {
    const target = this.querySelector('[data-sd-delivery-text]');
    if (!target) return;
    const min = parseInt(this.dataset.min, 10) || 4;
    const max = Math.max(parseInt(this.dataset.max, 10) || 7, min);
    const format = new Intl.DateTimeFormat('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
    const from = format.format(addWorkingDays(new Date(), min));
    const to = format.format(addWorkingDays(new Date(), max));
    const template = this.dataset.template || 'Delivered between {from} and {to}';
    target.innerHTML = escapeHtml(template)
      .replace('{from}', `<strong>${from}</strong>`)
      .replace('{to}', `<strong>${to}</strong>`);
  }
}

const UNIT_KEY = 'sd-size-unit';

/**
 * Inches / cm switch for the size chart (snippets/sd-size-chart.liquid).
 * Cells carry their inch value in data-in; cm is rounded to the nearest whole number.
 * The choice is remembered in this browser.
 */
class SdSizeChart extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    let unit = 'in';
    try {
      unit = localStorage.getItem(UNIT_KEY) === 'cm' ? 'cm' : 'in';
    } catch {}
    this.#apply(unit);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    const button = event.target.closest('[data-unit-choice]');
    if (!button) return;
    const unit = button.dataset.unitChoice;
    try {
      localStorage.setItem(UNIT_KEY, unit);
    } catch {}
    this.#apply(unit);
  };

  #apply(unit) {
    this.dataset.unit = unit;
    for (const button of this.querySelectorAll('[data-unit-choice]')) {
      button.setAttribute('aria-pressed', String(button.dataset.unitChoice === unit));
    }
    for (const cell of this.querySelectorAll('td[data-in]')) {
      const inches = parseFloat(cell.dataset.in);
      if (Number.isNaN(inches)) continue;
      cell.textContent = unit === 'cm' ? String(Math.round(inches * 2.54)) : cell.dataset.in;
    }
    const note = this.querySelector('.sd-size-chart__note');
    if (note) {
      note.dataset.inText ??= note.textContent;
      note.textContent = unit === 'cm' ? 'All measurements are in centimetres (converted from inches).' : note.dataset.inText;
    }
  }
}

const VARIANT_WAIT_MS = 4000;

/**
 * Product buy bar and size check (sections/sd-product-buy-bar.liquid).
 * - Shows a bar fixed to the bottom of the screen whenever the product's own buy buttons are off screen.
 * - Until the shopper picks a size (data-size-chosen="false"), "Add to cart" in the bar or in the buy box
 *   opens a size sheet; picking a size there selects it in the product's variant picker, waits for Horizon to
 *   update the form, then presses the form's own Add to cart button. "Buy it now" scrolls to the sizes instead.
 */
class SdBuyBar extends HTMLElement {
  /** @type {{id: number, size: string, available: boolean, price: string, compare: string | null, off: number}[]} */
  #variants = [];
  #sizeIndex = -1;
  #frame = 0;
  #pendingAdd = false;
  #adding = false;

  connectedCallback() {
    try {
      this.#variants = JSON.parse(this.querySelector('[data-sd-buy-bar-variants]')?.textContent || '[]');
    } catch {
      this.#variants = [];
    }
    this.#sizeIndex = parseInt(this.dataset.sizeIndex ?? '-1', 10);

    this.addEventListener('click', this.#onClick);
    this.#sheet?.addEventListener('close', this.#onSheetClose);
    document.addEventListener('click', this.#onDocumentClick, true);
    document.addEventListener('change', this.#onDocumentChange, true);
    window.addEventListener('scroll', this.#schedule, { passive: true });
    window.addEventListener('resize', this.#schedule, { passive: true });
    this.#schedule();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#sheet?.removeEventListener('close', this.#onSheetClose);
    document.removeEventListener('click', this.#onDocumentClick, true);
    document.removeEventListener('change', this.#onDocumentChange, true);
    window.removeEventListener('scroll', this.#schedule);
    window.removeEventListener('resize', this.#schedule);
    if (this.#frame) cancelAnimationFrame(this.#frame);
  }

  get #sheet() {
    return this.querySelector('[data-sd-sheet]');
  }

  get #chosen() {
    return this.dataset.sizeChosen !== 'false';
  }

  /** The product's own form in the main product section (not a quick-add dialog). */
  get #form() {
    const forms = document.querySelectorAll(
      `.product-information product-form-component[data-product-id="${this.dataset.productId}"]`
    );
    return [...forms].find((form) => !form.closest('dialog')) ?? null;
  }

  get #section() {
    return this.#form?.closest('.product-information') ?? null;
  }

  // Bar visibility: shown while the buy buttons are off screen
  #schedule = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(this.#updateVisibility);
  };

  #updateVisibility = () => {
    this.#frame = 0;
    const target = this.#form?.closest('.buy-buttons-block') ?? this.#form;
    let visible = false;
    if (target) {
      const rect = target.getBoundingClientRect();
      visible = rect.bottom < 0 || rect.top > window.innerHeight;
    }
    this.toggleAttribute('data-visible', visible);
    const bar = this.querySelector('.sd-buy-bar__bar');
    if (bar instanceof HTMLElement) bar.inert = !visible;
  };

  // Clicks inside the bar and the size sheet
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-sd-add]')) {
      if (this.#needsSize()) this.#openSheet(true);
      else this.#addToCart();
      return;
    }
    if (target.closest('[data-sd-open-sheet]')) {
      this.#openSheet(false);
      return;
    }
    const option = target.closest('[data-sd-size]');
    if (option instanceof HTMLButtonElement && !option.disabled) {
      const addAfter = this.#pendingAdd;
      this.#pendingAdd = false;
      this.#sheet?.close();
      this.#choose(option.dataset.sdSize ?? '', addAfter);
      return;
    }
    if (target.closest('[data-sd-close]')) {
      this.#sheet?.close();
      return;
    }
    if (target.closest('[data-sd-chart]')) {
      this.#sheet?.close();
      this.#section?.querySelector('.sd-size-help__chart button')?.click();
      return;
    }
    // A tap on the dimmed backdrop closes the sheet
    if (target === this.#sheet) this.#sheet?.close();
  };

  #onSheetClose = () => {
    this.#pendingAdd = false;
  };

  // Guard on the buy box while no size is picked
  #onDocumentClick = (event) => {
    const form = this.#form;
    const target = event.target;
    if (!form || !(target instanceof Element)) return;

    // A tap on a size in the product's own picker counts as choosing it, even the pre-selected one
    // (tapping an already checked radio fires no change event).
    const section = this.#section;
    if (section && section.contains(target) && target.closest('variant-picker')) {
      const input =
        target.closest('label')?.querySelector('input[type="radio"]') ?? target.closest('input[type="radio"]');
      if (input instanceof HTMLInputElement && this.#isSizeInput(input) && !input.disabled) {
        this.#markChosen(input.value);
      }
      return;
    }

    if (this.#chosen || this.#adding || !form.contains(target)) return;

    if (target.closest('[ref="addToCartButton"], .add-to-cart-button')) {
      event.preventDefault();
      event.stopPropagation();
      this.#openSheet(true);
    } else if (target.closest('.accelerated-checkout-block, shopify-accelerated-checkout, .shopify-payment-button')) {
      event.preventDefault();
      event.stopPropagation();
      this.#promptForSize();
    }
  };

  #onDocumentChange = (event) => {
    const target = event.target;
    const section = this.#section;
    if (!section || !(target instanceof Element) || !section.contains(target) || !target.closest('variant-picker')) {
      return;
    }
    if (target instanceof HTMLInputElement && this.#isSizeInput(target)) this.#markChosen(target.value);
    if (target instanceof HTMLSelectElement && this.#isSizeSelect(target)) this.#markChosen(target.value);
  };

  #needsSize() {
    return this.#sizeIndex >= 0 && !this.#chosen;
  }

  /** @param {HTMLInputElement} input */
  #isSizeInput(input) {
    const fieldsets = this.#section?.querySelectorAll('variant-picker fieldset') ?? [];
    if (fieldsets.length === 0) return this.#sizeIndex === 0;
    return fieldsets[this.#sizeIndex]?.contains(input) ?? false;
  }

  /** @param {HTMLSelectElement} select */
  #isSizeSelect(select) {
    const selects = this.#section?.querySelectorAll('variant-picker select') ?? [];
    return selects[this.#sizeIndex] === select;
  }

  /** @param {boolean} addAfter */
  #openSheet(addAfter) {
    const sheet = this.#sheet;
    if (!(sheet instanceof HTMLDialogElement)) {
      this.#promptForSize();
      return;
    }
    this.#pendingAdd = addAfter;
    sheet.showModal();
    const first = sheet.querySelector('[data-sd-size]:not(:disabled)');
    if (first instanceof HTMLElement) first.focus();
  }

  #promptForSize() {
    const section = this.#section;
    const picker = section?.querySelector('variant-picker');
    if (!section || !picker) return;
    if (!section.querySelector('.sd-size-prompt')) {
      const prompt = document.createElement('p');
      prompt.className = 'sd-size-prompt';
      prompt.setAttribute('role', 'alert');
      prompt.textContent = this.querySelector('[data-sd-prompt]')?.textContent?.trim() || 'Please select a size';
      picker.before(prompt);
    }
    picker.scrollIntoView({ block: 'center', behavior: 'smooth' });
    this.toggleAttribute('data-attention', true);
    setTimeout(() => this.toggleAttribute('data-attention', false), 900);
  }

  /** @param {string} size */
  #markChosen(size) {
    this.dataset.sizeChosen = 'true';
    this.#section?.querySelector('.sd-size-prompt')?.remove();
    const variant = this.#variants.find((v) => v.size === size);
    if (variant) this.#render(variant);
  }

  /** @param {{size: string, price: string, compare: string | null, off: number}} variant */
  #render(variant) {
    const label = this.querySelector('[data-sd-size-label]');
    if (label) label.textContent = `Size ${variant.size}`;
    const price = this.querySelector('[data-sd-price]');
    if (price) price.textContent = variant.price;
    const compare = this.querySelector('[data-sd-compare]');
    if (compare instanceof HTMLElement) {
      compare.textContent = variant.compare ?? '';
      compare.hidden = !variant.compare;
    }
    const off = this.querySelector('[data-sd-off]');
    if (off instanceof HTMLElement) {
      off.textContent = `${variant.off}% off`;
      off.hidden = !variant.off;
    }
  }

  /**
   * Selects the size in the product's own picker, then (optionally) adds to cart.
   * @param {string} size
   * @param {boolean} addAfter
   */
  async #choose(size, addAfter) {
    const variant = this.#variants.find((v) => v.size === size);
    const form = this.#form;
    if (!variant || !form) return;
    this.#markChosen(size);
    this.#setBusy(true);
    try {
      const idInput = form.querySelector('input[name="id"]');
      if (!(idInput instanceof HTMLInputElement) || idInput.value !== String(variant.id)) {
        this.#pickInPicker(size);
        await this.#waitForVariant(variant.id);
      }
      if (addAfter) this.#addToCart();
    } finally {
      this.#setBusy(false);
    }
  }

  /** @param {string} size */
  #pickInPicker(size) {
    const section = this.#section;
    if (!section) return;
    const fieldset = section.querySelectorAll('variant-picker fieldset')[this.#sizeIndex];
    const input = fieldset
      ? [...fieldset.querySelectorAll('input[type="radio"]')].find((i) => i instanceof HTMLInputElement && i.value === size)
      : null;
    if (input instanceof HTMLInputElement) {
      input.click();
      return;
    }
    const select = section.querySelectorAll('variant-picker select')[this.#sizeIndex];
    if (select instanceof HTMLSelectElement) {
      select.value = size;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Resolves once Horizon has put the variant id into the product form (or after a timeout).
   * @param {number} id
   */
  #waitForVariant(id) {
    return new Promise((resolve) => {
      const started = performance.now();
      const check = () => {
        const input = this.#form?.querySelector('input[name="id"]');
        const ready = input instanceof HTMLInputElement && input.value === String(id);
        if (ready || performance.now() - started > VARIANT_WAIT_MS) {
          resolve(undefined);
          return;
        }
        setTimeout(check, 60);
      };
      check();
    });
  }

  #addToCart() {
    const button = this.#form?.querySelector('[ref="addToCartButton"]');
    if (!(button instanceof HTMLButtonElement)) return;
    if (button.disabled) {
      button.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }
    this.#adding = true;
    try {
      button.click();
    } finally {
      this.#adding = false;
    }
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.querySelector('[data-sd-add]')?.setAttribute('aria-busy', String(busy));
  }
}

/**
 * Collection page intro (sections/sd-collection-intro.liquid).
 * - Shows "Read more" only when the description is taller than its line limit, and opens or closes it.
 * - When the row of collection links scrolls sideways (phones), brings the current collection into view.
 */
class SdCollectionIntro extends HTMLElement {
  /** @type {ResizeObserver | null} */
  #observer = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    const text = this.querySelector('[data-sd-desc-text]');
    if (text && 'ResizeObserver' in window) {
      this.#observer = new ResizeObserver(() => this.#measure());
      this.#observer.observe(text);
    }
    this.#measure();
    this.#revealCurrent();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #measure() {
    const wrap = this.querySelector('[data-sd-desc]');
    const text = this.querySelector('[data-sd-desc-text]');
    const toggle = this.querySelector('[data-sd-desc-toggle]');
    if (!wrap || !text || !(toggle instanceof HTMLElement) || wrap.hasAttribute('data-expanded')) return;
    const clamped = text.scrollHeight > text.clientHeight + 2;
    wrap.toggleAttribute('data-clamped', clamped);
    toggle.hidden = !clamped;
  }

  #onClick = (event) => {
    const toggle = event.target instanceof Element ? event.target.closest('[data-sd-desc-toggle]') : null;
    const wrap = this.querySelector('[data-sd-desc]');
    if (!(toggle instanceof HTMLElement) || !wrap) return;
    const expanded = !wrap.hasAttribute('data-expanded');
    wrap.toggleAttribute('data-expanded', expanded);
    toggle.setAttribute('aria-expanded', String(expanded));
    toggle.textContent = (expanded ? toggle.dataset.less : toggle.dataset.more) ?? '';
    if (expanded) wrap.removeAttribute('data-clamped');
    else this.#measure();
  };

  #revealCurrent() {
    const list = this.querySelector('[data-sd-links]');
    const current = list?.querySelector('[aria-current]');
    if (!(list instanceof HTMLElement) || !(current instanceof HTMLElement)) return;
    if (list.scrollWidth <= list.clientWidth) return;
    const offset = current.getBoundingClientRect().left - list.getBoundingClientRect().left;
    list.scrollLeft += offset - (list.clientWidth - current.offsetWidth) / 2;
  }
}

/**
 * @param {Date} date
 * @param {number} days
 */
function addWorkingDays(date, days) {
  const result = new Date(date);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    if (result.getDay() !== 0) added += 1;
  }
  return result;
}

/** @param {string} value */
function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

if (!customElements.get('sd-sticky-video')) customElements.define('sd-sticky-video', SdStickyVideo);
if (!customElements.get('sd-variant-sync')) customElements.define('sd-variant-sync', SdVariantSync);
if (!customElements.get('sd-delivery-date')) customElements.define('sd-delivery-date', SdDeliveryDate);
if (!customElements.get('sd-size-chart')) customElements.define('sd-size-chart', SdSizeChart);
if (!customElements.get('sd-buy-bar')) customElements.define('sd-buy-bar', SdBuyBar);
if (!customElements.get('sd-collection-intro')) customElements.define('sd-collection-intro', SdCollectionIntro);
